'use client'

import './Calculator.css'

import type { RateTable } from '@/utils/financing'
import type { IncomeRange } from '@/views/microsite-pf/pf/pfMicrositeShared'
import type { ChangeEvent } from 'react'
import type { CalculatorProps, PersonalFinancingRates } from './types'
import { calculatorCCSP, CustomSlider, marks } from '@/constants/calculator'

import { useEffect, useState } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import parse from 'html-react-parser'
import { isEmpty } from 'lodash'

import useStore from '@/stores/Store'
import api from '@/utils/api'
import {
  calculateMonthlyInstallment,
  formatInstallment,
  parseRateTable,
  validateFinancingAmount
} from '@/utils/financing'

const CalculatorCCSP = ({ onIncomeRangeChange, source }: CalculatorProps) => {
  const [incomeRange, setIncomeRange] = useState('below5k')
  const [financingAmount, setFinancingAmount] = useState('')
  const [tenure, setTenure] = useState(2)
  const [monthlyPayment, setMonthlyPayment] = useState('0.00')
  const [isActive, setIsActive] = useState(false)
  const [amountError, setAmountError] = useState('')
  const [isCalculated, setIsCalculated] = useState(false)
  const [incomeLow, setIncomeLow] = useState<RateTable>({})
  const [incomeHigh, setIncomeHigh] = useState<RateTable>({})
  const { calculatorState, setCalculatorState } = useStore()

  const handleIncomeChange = (range: IncomeRange) => {
    setIncomeRange(range)
    setTenure(
      range === 'below5k'
        ? Number.parseInt(Object.keys(incomeLow)[0])
        : Number.parseInt(Object.keys(incomeHigh)[0])
    )
    setFinancingAmount('')
    setMonthlyPayment('0.00')
    setAmountError('')
    onIncomeRangeChange?.(range)
  }

  const handleFinancingAmountChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replaceAll(/[^0-9.]/g, '')
    setFinancingAmount(value)
    setAmountError('')
  }

  const calculateTotal = () => {
    const result = validateFinancingAmount(financingAmount, {
      min: incomeRange === 'below5k' ? 5000 : 5000,
      max: incomeRange === 'below5k' ? 80000 : 250000
    })
    if (result.status === 'required') {
      setAmountError(calculatorCCSP.amountErrorRequired)
      return
    }
    if (result.status === 'out-of-range') {
      setAmountError(
        incomeRange === 'below5k'
          ? calculatorCCSP.amountErrorRangeBelow5k
          : calculatorCCSP.amountErrorRangeAbove5k
      )
      return
    }
    const amount = result.amount

    // const repaymentRates =
    //   incomeRange === "below5k"
    //     ? {
    //         2: 5.45 / 100,
    //         3: 5.55 / 100,
    //         4: 5.67 / 100,
    //         5: 5.82 / 100,
    //         6: 6.0 / 100,
    //         7: 6.2 / 100,
    //         8: 6.39 / 100,
    //       }
    //     : {
    //         1: 5.1 / 100,
    //         2: 5.45 / 100,
    //         3: 5.55 / 100,
    //         4: 5.67 / 100,
    //         5: 5.82 / 100,
    //         6: 6.0 / 100,
    //         7: 6.2 / 100,
    //         8: 6.39 / 100,
    //         9: 6.57 / 100,
    //         10: 6.77 / 100,
    //       };
    const repaymentRates = incomeRange === 'below5k' ? incomeLow : incomeHigh

    const rate = repaymentRates[tenure]
    const total = calculateMonthlyInstallment(amount, rate, tenure)

    setMonthlyPayment(formatInstallment(total))
    setFinancingAmount(amount.toString())
    setIsCalculated(true)
    setAmountError('')
  }

  useEffect(() => {
    if (isCalculated) {
      calculateTotal()
    }
  }, [tenure])

  const handleAssign = (data: PersonalFinancingRates) => {
    const incomeLowData =
      source === 'EBS'
        ? data.PersonalFinancingIEBS?.[0]?.incomeLowRange
        : data.PersonalFinancingICCSPSD?.[0]?.incomeLowRange
    const incomeHighData =
      source === 'EBS'
        ? data.PersonalFinancingIEBS?.[0]?.incomeHighRange
        : data.PersonalFinancingICCSPSD?.[0]?.incomeHighRange

    // Transform the array into an object with { item.tenure: item.rate }
    const incomeLowObject = parseRateTable(incomeLowData)
    const incomeHighObject = parseRateTable(incomeHighData)

    // Set the state with the transformed object
    setIncomeLow(incomeLowObject)
    setIncomeHigh(incomeHighObject)
    setTenure(
      incomeRange === 'below5k'
        ? Number.parseInt(Object.keys(incomeLowObject)[0])
        : Number.parseInt(Object.keys(incomeHighObject)[0])
    )
  }

  const fetchFinanceData = async () => {
    try {
      const response = await api.get('/personal-financings?pLevel')
      setCalculatorState(response.data.data[0])

      handleAssign(response.data.data[0])
    } catch (error) {
      console.error('Error fetching data', error)
    }
  }

  // DELIBERATE CLIENT REFETCH (derived 2026-08-17 from Task 5) — do not remove.
  // Profit rates drive a figure the customer acts on; publish-to-deploy latency
  // (3-15 min) is too long to compute against a superseded rate. The host routes
  // are excluded from tests/no-client-cms-fetch.spec.ts. See Task 9 of
  // docs/superpowers/plans/2026-08-17-remove-client-side-cms-refetch.md
  useEffect(() => {
    if (isEmpty(calculatorState)) {
      fetchFinanceData()
    } else {
      handleAssign(calculatorState)
    }
  }, [source])

  return (
    <div id="calculator" className="calculator section-calc light-background calculator-form">
      <div data-aos="fade-up" data-aos-delay="200" className="w-100">
        <p className="header">{calculatorCCSP.header}</p>
        <div className="btn-group w-100" role="group" aria-label="Basic radio toggle button group">
          <input
            type="radio"
            className="btn-check"
            name="btnradio"
            id="btnradio1"
            checked={incomeRange === 'below5k'}
            onChange={() => handleIncomeChange('below5k')}
          />
          <label
            className="income btn btn-outline-primary"
            htmlFor="btnradio1"
            onClick={() => handleIncomeChange('below5k')}
          >
            {parse(calculatorCCSP.incomeBelow5k)}
          </label>

          <input
            type="radio"
            className="btn-check"
            name="btnradio"
            id="btnradio2"
            checked={incomeRange === 'above5k'}
            onChange={() => handleIncomeChange('above5k')}
          />
          <label
            className="income btn btn-outline-primary"
            htmlFor="btnradio2"
            onClick={() => handleIncomeChange('above5k')}
          >
            {parse(calculatorCCSP.incomeAbove5k)}
          </label>
        </div>

        <div className={`form-group-calc ${isActive ? 'active' : ''}`}>
          <label htmlFor="financing-amount" className="income-group income-group-text ">
            {incomeRange === 'below5k'
              ? calculatorCCSP.financingAmountBelow5k
              : calculatorCCSP.financingAmountAbove5k}
          </label>
          <div className="input-group">
            <span className="amount">RM</span>
            <input
              type="text"
              id="financing-amount"
              name="financing-amount"
              placeholder={calculatorCCSP.insertAmountPlaceholder}
              className={`form-control-input ${amountError ? 'input-error' : ''}`}
              value={financingAmount}
              onChange={handleFinancingAmountChange}
              onFocus={() => setIsActive(true)}
              onBlur={() => setIsActive(false)}
              style={{ border: 'none' }}
            />
          </div>
          {amountError && <div className="error-message">{amountError}</div>}
        </div>

        <div className="form-group-2">
          <label htmlFor="tenure" className="tenure">
            {calculatorCCSP.tenureLabel}
          </label>
          <div className="input-group">
            <CustomSlider
              value={tenure}
              onChange={(_: unknown, value: number) => {
                setTenure(value)
                setIsCalculated(true)
              }}
              step={1}
              marks={marks}
              min={
                incomeRange === 'below5k'
                  ? Number.parseInt(Object.keys(incomeLow)[0])
                  : Number.parseInt(Object.keys(incomeHigh)[0])
              }
              max={
                incomeRange === 'below5k'
                  ? Number.parseInt(Object.keys(incomeLow)[Object.keys(incomeLow).length - 1])
                  : Number.parseInt(Object.keys(incomeHigh)[Object.keys(incomeHigh).length - 1])
              }
              valueLabelDisplay="off"
              aria-label="Financing Tenure"
            />
            {/* <CustomSlider
              value={tenure}
              onChange={(_: unknown, value: number) => {
                setTenure(value);
                setIsCalculated(true);
              }}
              step={1}
              marks={marks}
              min={minTenure}
              max={maxTenure}
              valueLabelDisplay="off"
              aria-label="Financing Tenure"
            /> */}
          </div>
          <p className="payment">{calculatorCCSP.monthlyPaymentLabel}</p>

          <Container>
            <Row className="align-items-center justify-content-between row-cols-2">
              <Col xs={8} className="ps-0">
                <div className="form-group-3">
                  <div className="input-group center-input-group">
                    <span className="input-group-text-2">RM</span>
                    <span className="financing-amount-style">{monthlyPayment}</span>
                  </div>
                </div>
              </Col>
              <Col xs={4} className="pe-0">
                <button
                  type="button"
                  className="btn btn-primary calculate-button w-100"
                  onClick={calculateTotal}
                >
                  {calculatorCCSP.calculateTotalButton}
                </button>
              </Col>
            </Row>
          </Container>
        </div>
        <span className="notes">{calculatorCCSP.notes}</span>
      </div>
    </div>
  )
}

export default CalculatorCCSP
